import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Clock, Building2, Users, LogOut, TrendingUp, DollarSign, Activity,
  User, Mail, Lock, Save, Ticket, ChevronRight, Eye, CreditCard,
  Calendar, Globe, Phone, MapPin, BarChart3, PieChart, ArrowUpRight,
  ArrowDownRight, Search, Filter, RefreshCw, CheckCircle, XCircle,
  AlertTriangle, PlayCircle, PauseCircle, Briefcase, ExternalLink,
  UserCog, Send, LineChart, Megaphone, Plus, Trash2, Edit, Bell, Download, ToggleLeft, ToggleRight, Settings2
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { format, formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuperAdminDashboardNew = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Stats
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [industryStats, setIndustryStats] = useState(null);
  const [growthMetrics, setGrowthMetrics] = useState(null);
  const [revenueAnalytics, setRevenueAnalytics] = useState(null);
  const [healthScores, setHealthScores] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [allTags, setAllTags] = useState([]);
  
  // Dialogs
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetailOpen, setCompanyDetailOpen] = useState(false);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [extendTrialDialogOpen, setExtendTrialDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [trialDays, setTrialDays] = useState(14);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Notes & Tags
  const [companyNotes, setCompanyNotes] = useState([]);
  const [companyTags, setCompanyTags] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newTag, setNewTag] = useState('');
  
  // Subdomain
  const [companySubdomain, setCompanySubdomain] = useState('');
  const [subdomainInfo, setSubdomainInfo] = useState(null);
  
  // Edit Company Info
  const [editCompanyDialogOpen, setEditCompanyDialogOpen] = useState(false);
  const [editCompanyForm, setEditCompanyForm] = useState({
    industry: '',
    company_size: '',
    timezone: '',
    phone: '',
    abn_tax_id: '',
    billing_email: '',
    referral_source: '',
    previous_solution: '',
    multiple_locations: false
  });
  
  // Impersonation
  const [impersonationDialogOpen, setImpersonationDialogOpen] = useState(false);
  const [selectedUserForImpersonation, setSelectedUserForImpersonation] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({ email: '', name: '' });
  const [passwordForm, setPasswordForm] = useState({ 
    current_password: '', 
    new_password: '', 
    confirm_password: '' 
  });
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    message: '',
    type: 'info',
    is_banner: true,
    is_dismissible: true,
    target_audience: 'all',
    expires_at: '',
    send_email: false
  });

  // Feature Flags
  const [featureFlagsDialogOpen, setFeatureFlagsDialogOpen] = useState(false);
  const [companyFeatureFlags, setCompanyFeatureFlags] = useState(null);
  
  // Notes & Tags Dialog
  const [notesTagsDialogOpen, setNotesTagsDialogOpen] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  
  // Subdomain Dialog
  const [subdomainDialogOpen, setSubdomainDialogOpen] = useState(false);
  const [loadingSubdomain, setLoadingSubdomain] = useState(false);

  const FEATURE_FLAG_LABELS = {
    facial_recognition: { label: 'Facial Recognition', description: 'Face verification on clock-in' },
    shift_swaps: { label: 'Shift Swaps', description: 'Allow employees to swap shifts' },
    ai_chatbot: { label: 'AI Chatbot', description: 'AI-powered support assistant' },
    push_notifications: { label: 'Push Notifications', description: 'Mobile push notifications' },
    roster_builder: { label: 'Roster Builder', description: 'Schedule management tools' },
    break_tracking: { label: 'Break Tracking', description: 'Track lunch and breaks' },
    overtime_alerts: { label: 'Overtime Alerts', description: 'Overtime notifications' },
    payroll_export: { label: 'Payroll Export', description: 'Export to payroll systems' }
  };

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, companiesRes, referralRes, industryRes, growthRes, announcementsRes, revenueRes, tagsRes, healthRes] = await Promise.all([
        axios.get(`${API}/super-admin/stats`),
        axios.get(`${API}/super-admin/companies/detailed`),
        axios.get(`${API}/super-admin/referral-stats`),
        axios.get(`${API}/super-admin/industry-stats`),
        axios.get(`${API}/super-admin/growth-metrics`),
        axios.get(`${API}/super-admin/announcements`),
        axios.get(`${API}/super-admin/revenue-analytics`),
        axios.get(`${API}/super-admin/tags`),
        axios.get(`${API}/super-admin/company-health-scores`)
      ]);
      
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
      setReferralStats(referralRes.data);
      setIndustryStats(industryRes.data);
      setGrowthMetrics(growthRes.data);
      setAnnouncements(announcementsRes.data);
      setRevenueAnalytics(revenueRes.data);
      setAllTags(tagsRes.data);
      setHealthScores(healthRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    if (user) {
      setProfileForm({ email: user.email, name: user.name });
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleUpdateSubscription = async (newStatus) => {
    if (!selectedCompany) return;
    setIsUpdating(true);
    try {
      await axios.put(`${API}/super-admin/companies/${selectedCompany.id}/subscription`, {
        subscription_status: newStatus
      });
      toast.success(`Subscription updated to "${newStatus}"`);
      setSubscriptionDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleExtendTrial = async () => {
    if (!selectedCompany) return;
    setIsUpdating(true);
    try {
      await axios.post(`${API}/super-admin/companies/${selectedCompany.id}/extend-trial?days=${trialDays}`);
      toast.success(`Trial extended by ${trialDays} days`);
      setExtendTrialDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend trial');
    } finally {
      setIsUpdating(false);
    }
  };

  const fetchCompanyUsers = async (companyId) => {
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/super-admin/companies/${companyId}/users`);
      setCompanyUsers(res.data.filter(u => u.role !== 'super_admin'));
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setCompanyUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleImpersonateUser = async (userId) => {
    setIsUpdating(true);
    try {
      const res = await axios.post(`${API}/super-admin/impersonate/${userId}`);
      const { token, user: impersonatedUser } = res.data;
      
      // Store the impersonation token
      localStorage.setItem('impersonation_token', token);
      localStorage.setItem('original_token', localStorage.getItem('token'));
      localStorage.setItem('token', token);
      
      // Store impersonated user info for the banner
      localStorage.setItem('impersonated_user', JSON.stringify(impersonatedUser));
      
      toast.success(`Now viewing as ${impersonatedUser.name} (${impersonatedUser.email})`);
      
      // Use window.location for full page reload to reset app state
      if (impersonatedUser.role === 'company_admin') {
        window.location.href = '/admin';
      } else {
        window.location.href = '/dashboard';
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
      setIsUpdating(false);
    }
  };

  const handleSendTrialReminder = async (companyId) => {
    setIsUpdating(true);
    try {
      await axios.post(`${API}/super-admin/send-trial-reminder/${companyId}`);
      toast.success('Trial reminder email sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setIsUpdating(false);
    }
  };

  const openEditCompanyDialog = () => {
    if (selectedCompany) {
      setEditCompanyForm({
        industry: selectedCompany.industry || '',
        company_size: selectedCompany.company_size || '',
        timezone: selectedCompany.timezone || '',
        phone: selectedCompany.phone || '',
        abn_tax_id: selectedCompany.abn_tax_id || '',
        billing_email: selectedCompany.billing_email || '',
        referral_source: selectedCompany.referral_source || '',
        previous_solution: selectedCompany.previous_solution || '',
        multiple_locations: selectedCompany.multiple_locations || false
      });
      setEditCompanyDialogOpen(true);
    }
  };

  const handleSaveCompanyInfo = async () => {
    if (!selectedCompany) return;
    setIsUpdating(true);
    try {
      await axios.put(`${API}/super-admin/companies/${selectedCompany.id}/info`, editCompanyForm);
      toast.success('Company information updated!');
      setEditCompanyDialogOpen(false);
      // Refresh company data
      const res = await axios.get(`${API}/super-admin/companies/detailed`);
      setCompanies(res.data);
      // Update selected company
      const updated = res.data.find(c => c.id === selectedCompany.id);
      if (updated) setSelectedCompany(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company info');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsUpdatingProfile(true);
    try {
      await axios.put(`${API}/auth/profile`, {
        email: profileForm.email,
        name: profileForm.name
      });
      toast.success('Profile updated successfully!');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setIsChangingPassword(true);
    try {
      await axios.put(`${API}/auth/password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Announcement handlers
  const resetAnnouncementForm = () => {
    setAnnouncementForm({
      title: '',
      message: '',
      type: 'info',
      is_banner: true,
      is_dismissible: true,
      target_audience: 'all',
      expires_at: '',
      send_email: false
    });
    setEditingAnnouncement(null);
  };

  const handleCreateAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.message) {
      toast.error('Title and message are required');
      return;
    }
    setIsUpdating(true);
    try {
      const payload = {
        ...announcementForm,
        expires_at: announcementForm.expires_at || null
      };
      
      if (editingAnnouncement) {
        await axios.put(`${API}/super-admin/announcements/${editingAnnouncement.id}`, payload);
        toast.success('Announcement updated');
      } else {
        await axios.post(`${API}/super-admin/announcements`, payload);
        toast.success(announcementForm.send_email ? 'Announcement created and emails sent!' : 'Announcement created');
      }
      setAnnouncementDialogOpen(false);
      resetAnnouncementForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save announcement');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    setAnnouncementForm({
      title: announcement.title,
      message: announcement.message,
      type: announcement.type,
      is_banner: announcement.is_banner,
      is_dismissible: announcement.is_dismissible,
      target_audience: announcement.target_audience,
      expires_at: announcement.expires_at || '',
      send_email: false
    });
    setAnnouncementDialogOpen(true);
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await axios.delete(`${API}/super-admin/announcements/${announcementId}`);
      toast.success('Announcement deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete announcement');
    }
  };

  const handleToggleAnnouncementActive = async (announcement) => {
    try {
      await axios.put(`${API}/super-admin/announcements/${announcement.id}`, {
        is_active: !announcement.is_active
      });
      toast.success(announcement.is_active ? 'Announcement deactivated' : 'Announcement activated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update announcement');
    }
  };

  // Export company data
  const handleExportCompanyData = async (companyId, companyName) => {
    setIsUpdating(true);
    try {
      const response = await axios.get(`${API}/super-admin/companies/${companyId}/export`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const safeName = companyName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
      link.setAttribute('download', `company_export_${safeName}_${date}.zip`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Data exported for ${companyName}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export company data');
    } finally {
      setIsUpdating(false);
    }
  };

  // Feature flags handlers
  const fetchCompanyFeatureFlags = async (companyId) => {
    try {
      const res = await axios.get(`${API}/super-admin/companies/${companyId}/feature-flags`);
      setCompanyFeatureFlags(res.data);
    } catch (error) {
      toast.error('Failed to load feature flags');
      setCompanyFeatureFlags(null);
    }
  };

  const handleToggleFeatureFlag = async (flagName, currentValue) => {
    if (!companyFeatureFlags) return;
    
    try {
      const res = await axios.put(
        `${API}/super-admin/companies/${companyFeatureFlags.company_id}/feature-flags`,
        { [flagName]: !currentValue }
      );
      setCompanyFeatureFlags({
        ...companyFeatureFlags,
        feature_flags: res.data.feature_flags
      });
      toast.success(`${FEATURE_FLAG_LABELS[flagName]?.label || flagName} ${!currentValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update feature flag');
    }
  };

  // Notes & Tags handlers
  const fetchCompanyNotesAndTags = async (companyId) => {
    setLoadingNotes(true);
    try {
      const res = await axios.get(`${API}/super-admin/companies/${companyId}/notes`);
      setCompanyNotes(res.data.notes || []);
      setCompanyTags(res.data.tags || []);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
      setCompanyNotes([]);
      setCompanyTags([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleAddNote = async () => {
    if (!selectedCompany || !newNote.trim()) return;
    try {
      const res = await axios.post(`${API}/super-admin/companies/${selectedCompany.id}/notes`, {
        note: newNote.trim()
      });
      setCompanyNotes([...companyNotes, res.data]);
      setNewNote('');
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!selectedCompany) return;
    try {
      await axios.delete(`${API}/super-admin/companies/${selectedCompany.id}/notes/${noteId}`);
      setCompanyNotes(companyNotes.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleUpdateTags = async () => {
    if (!selectedCompany) return;
    const tagsArray = newTag ? [...companyTags, newTag.trim().toLowerCase()] : companyTags;
    try {
      const res = await axios.put(`${API}/super-admin/companies/${selectedCompany.id}/tags`, {
        tags: tagsArray
      });
      setCompanyTags(res.data.tags);
      setNewTag('');
      toast.success('Tags updated');
      fetchAllData(); // Refresh all tags
    } catch (error) {
      toast.error('Failed to update tags');
    }
  };

  const handleRemoveTag = async (tagToRemove) => {
    if (!selectedCompany) return;
    const newTags = companyTags.filter(t => t !== tagToRemove);
    try {
      const res = await axios.put(`${API}/super-admin/companies/${selectedCompany.id}/tags`, {
        tags: newTags
      });
      setCompanyTags(res.data.tags);
      toast.success('Tag removed');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to remove tag');
    }
  };

  // Subdomain handlers
  const fetchCompanySubdomain = async (companyId) => {
    setLoadingSubdomain(true);
    try {
      const res = await axios.get(`${API}/super-admin/companies/${companyId}/subdomain`);
      setSubdomainInfo(res.data);
      setCompanySubdomain(res.data.subdomain || '');
    } catch (error) {
      console.error('Failed to fetch subdomain:', error);
      setSubdomainInfo(null);
      setCompanySubdomain('');
    } finally {
      setLoadingSubdomain(false);
    }
  };

  const handleSetSubdomain = async () => {
    if (!selectedCompany || !companySubdomain.trim()) return;
    setIsUpdating(true);
    try {
      const res = await axios.put(`${API}/super-admin/companies/${selectedCompany.id}/subdomain`, {
        subdomain: companySubdomain.trim().toLowerCase()
      });
      setSubdomainInfo(res.data);
      toast.success(`Subdomain set to ${res.data.full_url}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set subdomain');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveSubdomain = async () => {
    if (!selectedCompany) return;
    if (!window.confirm('Are you sure you want to remove the subdomain?')) return;
    setIsUpdating(true);
    try {
      await axios.delete(`${API}/super-admin/companies/${selectedCompany.id}/subdomain`);
      setSubdomainInfo(null);
      setCompanySubdomain('');
      toast.success('Subdomain removed');
    } catch (error) {
      toast.error('Failed to remove subdomain');
    } finally {
      setIsUpdating(false);
    }
  };

  // Delete company handler
  const handleDeleteCompany = async () => {
    if (!selectedCompany) return;
    
    const companyName = selectedCompany.name;
    const hasTimeEntries = selectedCompany.time_entries_count > 0;
    
    if (hasTimeEntries) {
      toast.error(`Cannot delete ${companyName} - has ${selectedCompany.time_entries_count} time entries. Deactivate instead.`);
      return;
    }
    
    const confirmMessage = `Are you sure you want to PERMANENTLY DELETE "${companyName}"?\n\nThis will delete:\n- All company users\n- All company offices\n- All pending invites\n\nThis action cannot be undone!`;
    
    if (!window.confirm(confirmMessage)) return;
    
    // Double confirmation for safety
    const typedName = window.prompt(`Type the company name "${companyName}" to confirm deletion:`);
    if (typedName !== companyName) {
      toast.error('Company name did not match. Deletion cancelled.');
      return;
    }
    
    setIsUpdating(true);
    try {
      await axios.delete(`${API}/companies/${selectedCompany.id}`);
      toast.success(`Company "${companyName}" has been permanently deleted`);
      setCompanyDetailOpen(false);
      setSelectedCompany(null);
      fetchAllData(); // Refresh the company list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    } finally {
      setIsUpdating(false);
    }
  };

  // Filter companies
  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         company.industry?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && company.active) ||
                         (statusFilter === 'inactive' && !company.active);
    const matchesSubscription = subscriptionFilter === 'all' || 
                               company.subscription_status === subscriptionFilter;
    const matchesTag = tagFilter === 'all' || 
                      (company.tags && company.tags.includes(tagFilter));
    return matchesSearch && matchesStatus && matchesSubscription && matchesTag;
  });

  const getSubscriptionBadge = (status) => {
    const badges = {
      trial: { label: 'Trial', className: 'bg-blue-100 text-blue-700' },
      active: { label: 'Paid', className: 'bg-green-100 text-green-700' },
      cancelled: { label: 'Cancelled', className: 'bg-slate-100 text-slate-700' },
      suspended: { label: 'Suspended', className: 'bg-red-100 text-red-700' }
    };
    const badge = badges[status] || { label: status, className: 'bg-slate-100 text-slate-700' };
    return <Badge className={badge.className}>{badge.label}</Badge>;
  };

  const StatCard = ({ icon: Icon, label, value, subValue, trend, color = 'indigo' }) => (
    <Card className="border-0 shadow-md hover:shadow-lg transition-shadow" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{label}</p>
            <p className="text-3xl font-bold text-slate-900">{value}</p>
            {subValue && <p className="text-sm text-slate-500 mt-1">{subValue}</p>}
          </div>
          <div className={`w-12 h-12 rounded-xl bg-${color}-100 flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-3 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            <span>{Math.abs(trend)} this month</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center text-white">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <span className="font-bold text-slate-900">ClockIn Pro</span>
              <p className="text-xs text-slate-500">Super Admin Console</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/super-admin/error-logs')} data-testid="error-logs-btn">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Error Logs
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/super-admin/backups')} data-testid="backups-btn">
              <Download className="w-4 h-4 mr-2" />
              Backups
            </Button>
            <Button variant="outline" size="sm" onClick={fetchAllData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <span className="text-sm text-slate-600">{user?.name}</span>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-4xl grid-cols-7 mb-8">
            <TabsTrigger value="overview" data-testid="overview-tab">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="companies" data-testid="companies-tab">
              <Building2 className="w-4 h-4 mr-2" />
              Companies
            </TabsTrigger>
            <TabsTrigger value="billing" data-testid="billing-tab">
              <CreditCard className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
            <TabsTrigger value="announcements" data-testid="announcements-tab">
              <Megaphone className="w-4 h-4 mr-2" />
              Announce
            </TabsTrigger>
            <TabsTrigger value="support" data-testid="support-tab">
              <Ticket className="w-4 h-4 mr-2" />
              Support
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="profile-tab">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="health" data-testid="health-tab">
              <Activity className="w-4 h-4 mr-2" />
              Health
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : stats && (
              <>
                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard 
                    icon={DollarSign} 
                    label="Monthly Revenue" 
                    value={`$${stats.mrr.toFixed(0)}`}
                    subValue={`${stats.paid_companies} paid companies`}
                    color="green"
                  />
                  <StatCard 
                    icon={Building2} 
                    label="Total Companies" 
                    value={stats.total_companies}
                    trend={stats.companies_this_month}
                    color="indigo"
                  />
                  <StatCard 
                    icon={Users} 
                    label="Total Users" 
                    value={stats.total_users}
                    trend={stats.users_this_month}
                    color="blue"
                  />
                  <StatCard 
                    icon={Activity} 
                    label="Time Entries" 
                    value={stats.total_time_entries.toLocaleString()}
                    color="purple"
                  />
                </div>

                {/* Subscription & User Breakdown */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Subscription Status */}
                  <Card className="border-0 shadow-md">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5" />
                        Subscription Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <PlayCircle className="w-5 h-5 text-blue-600" />
                          <span className="font-medium">Trial</span>
                        </div>
                        <span className="text-2xl font-bold text-blue-600">{stats.trial_companies}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="font-medium">Paid / Active</span>
                        </div>
                        <span className="text-2xl font-bold text-green-600">{stats.paid_companies}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <PauseCircle className="w-5 h-5 text-slate-500" />
                          <span className="font-medium">Inactive</span>
                        </div>
                        <span className="text-2xl font-bold text-slate-500">{stats.inactive_companies}</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* User Breakdown */}
                  <Card className="border-0 shadow-md">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        User Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <User className="w-5 h-5 text-indigo-600" />
                          <span className="font-medium">Company Admins</span>
                        </div>
                        <span className="text-2xl font-bold text-indigo-600">{stats.total_admins}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <User className="w-5 h-5 text-purple-600" />
                          <span className="font-medium">Managers</span>
                        </div>
                        <span className="text-2xl font-bold text-purple-600">{stats.total_managers}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-cyan-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <User className="w-5 h-5 text-cyan-600" />
                          <span className="font-medium">Employees</span>
                        </div>
                        <span className="text-2xl font-bold text-cyan-600">{stats.total_employees}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Industry & Referral Stats */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Growth Chart */}
                  {growthMetrics && growthMetrics.growth && (
                    <Card className="border-0 shadow-md md:col-span-2">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <LineChart className="w-5 h-5" />
                          6-Month Growth Trend
                        </CardTitle>
                        <CardDescription>Companies and users growth over time</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {/* Chart Legend */}
                          <div className="flex gap-6 justify-end">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-indigo-500 rounded-full" />
                              <span className="text-sm text-slate-600">Companies</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-cyan-500 rounded-full" />
                              <span className="text-sm text-slate-600">Users</span>
                            </div>
                          </div>
                          
                          {/* Simple Bar Chart */}
                          <div className="flex items-end justify-between gap-2 h-48 pt-4">
                            {growthMetrics.growth.map((month, idx) => {
                              const maxCompanies = Math.max(...growthMetrics.growth.map(m => m.companies), 1);
                              const maxUsers = Math.max(...growthMetrics.growth.map(m => m.users), 1);
                              const companyHeight = (month.companies / maxCompanies) * 100;
                              const userHeight = (month.users / maxUsers) * 100;
                              
                              return (
                                <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                                  <div className="flex gap-1 items-end h-36 w-full">
                                    <div 
                                      className="flex-1 bg-indigo-500 rounded-t-sm transition-all hover:bg-indigo-600"
                                      style={{ height: `${Math.max(companyHeight, 5)}%` }}
                                      title={`${month.companies} companies`}
                                    />
                                    <div 
                                      className="flex-1 bg-cyan-500 rounded-t-sm transition-all hover:bg-cyan-600"
                                      style={{ height: `${Math.max(userHeight, 5)}%` }}
                                      title={`${month.users} users`}
                                    />
                                  </div>
                                  <div className="text-xs text-slate-500 text-center">
                                    {month.month}
                                  </div>
                                  <div className="flex gap-2 text-xs">
                                    <span className="text-indigo-600 font-medium">{month.companies}</span>
                                    <span className="text-cyan-600 font-medium">{month.users}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Industry Distribution */}
                  {industryStats && (
                    <Card className="border-0 shadow-md">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Briefcase className="w-5 h-5" />
                          Industries
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {industryStats.industries.slice(0, 6).map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                              <span className="text-sm text-slate-600">{item.industry}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-indigo-500 rounded-full"
                                    style={{ width: `${(item.count / industryStats.total_companies) * 100}%` }}
                                  />
                                </div>
                                <span className="text-sm font-medium w-8 text-right">{item.count}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Referral Sources */}
                  {referralStats && (
                    <Card className="border-0 shadow-md">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <TrendingUp className="w-5 h-5" />
                          How They Found Us
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {referralStats.referral_sources.slice(0, 6).map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                              <span className="text-sm text-slate-600">{item.source}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-green-500 rounded-full"
                                    style={{ width: `${(item.count / referralStats.total_companies) * 100}%` }}
                                  />
                                </div>
                                <span className="text-sm font-medium w-8 text-right">{item.count}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </>
            )}
          </TabsContent>

          {/* Companies Tab */}
          <TabsContent value="companies" className="space-y-6">
            {/* Filters */}
            <Card className="border-0 shadow-md">
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search companies..."
                        className="pl-10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        data-testid="search-companies"
                      />
                    </div>
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[150px]" data-testid="status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={subscriptionFilter} onValueChange={setSubscriptionFilter}>
                    <SelectTrigger className="w-[150px]" data-testid="subscription-filter">
                      <SelectValue placeholder="Subscription" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Plans</SelectItem>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="active">Paid</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                  {allTags.length > 0 && (
                    <Select value={tagFilter} onValueChange={setTagFilter}>
                      <SelectTrigger className="w-[150px]" data-testid="tag-filter">
                        <SelectValue placeholder="Filter by Tag" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Tags</SelectItem>
                        {allTags.map(t => (
                          <SelectItem key={t.tag} value={t.tag}>
                            {t.tag} ({t.count})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button variant="outline" onClick={() => navigate('/super-admin/companies')} data-testid="manage-companies-btn">
                    Manage Companies
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Companies List */}
            <Card className="border-0 shadow-md">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Companies ({filteredCompanies.length})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  </div>
                ) : filteredCompanies.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No companies found</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[500px]">
                    <div className="divide-y">
                      {filteredCompanies.map((company) => (
                        <div 
                          key={company.id}
                          className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => {
                            setSelectedCompany(company);
                            setCompanyDetailOpen(true);
                          }}
                          data-testid={`company-row-${company.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl ${company.active ? 'bg-indigo-600' : 'bg-slate-400'} text-white flex items-center justify-center font-bold text-lg`}>
                              {company.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-slate-900">{company.name}</p>
                                {company.active ? (
                                  <Badge variant="outline" className="text-green-600 border-green-200">Active</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-slate-500">Inactive</Badge>
                                )}
                                {getSubscriptionBadge(company.subscription_status)}
                                {company.subdomain && (
                                  <Badge className="bg-purple-100 text-purple-700 text-xs">{company.subdomain}</Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                                <span>{company.industry || 'No industry'}</span>
                                <span>•</span>
                                <span>{company.employee_count_actual} users</span>
                                <span>•</span>
                                <span>{company.time_entries_count.toLocaleString()} entries</span>
                                {company.tags && company.tags.length > 0 && (
                                  <>
                                    <span>•</span>
                                    <div className="flex gap-1">
                                      {company.tags.slice(0, 3).map(tag => (
                                        <Badge key={tag} variant="outline" className="text-xs py-0">{tag}</Badge>
                                      ))}
                                      {company.tags.length > 3 && (
                                        <span className="text-xs text-slate-400">+{company.tags.length - 3}</span>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm text-slate-500">
                                {company.last_activity 
                                  ? `Active ${formatDistanceToNow(new Date(company.last_activity), { addSuffix: true })}`
                                  : 'No activity'
                                }
                              </p>
                              <p className="text-xs text-slate-400">
                                Joined {format(new Date(company.created_at), 'dd/MM/yyyy')}
                              </p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="space-y-6">
            {stats && (
              <>
                {/* Revenue Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-0 shadow-md bg-gradient-to-br from-green-500 to-emerald-600 text-white">
                    <CardContent className="pt-6">
                      <p className="text-green-100 text-sm">Monthly Recurring Revenue</p>
                      <p className="text-4xl font-bold mt-2">${stats.mrr.toFixed(0)}</p>
                      <p className="text-green-100 text-sm mt-2">{stats.paid_companies} paying customers</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md">
                    <CardContent className="pt-6">
                      <p className="text-slate-500 text-sm">Annual Run Rate</p>
                      <p className="text-4xl font-bold text-slate-900 mt-2">${(stats.mrr * 12).toFixed(0)}</p>
                      <p className="text-slate-500 text-sm mt-2">Projected yearly revenue</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md">
                    <CardContent className="pt-6">
                      <p className="text-slate-500 text-sm">Pricing Model</p>
                      <p className="text-4xl font-bold text-slate-900 mt-2">$14<span className="text-xl font-normal">/mo</span></p>
                      <p className="text-slate-500 text-sm mt-2">Base (15 users) + $1/extra</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Trial Companies */}
                <Card className="border-0 shadow-md">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PlayCircle className="w-5 h-5 text-blue-600" />
                      Companies on Trial ({stats.trial_companies})
                    </CardTitle>
                    <CardDescription>
                      Companies currently in their free trial period
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {companies.filter(c => c.subscription_status === 'trial').length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No companies on trial</p>
                    ) : (
                      <div className="space-y-3">
                        {companies.filter(c => c.subscription_status === 'trial').map(company => (
                          <div key={company.id} className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center font-semibold">
                                {company.name.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium">{company.name}</p>
                                <p className="text-sm text-slate-500">
                                  {company.trial_ends_at 
                                    ? `Trial ends ${format(new Date(company.trial_ends_at), 'dd/MM/yyyy')}`
                                    : 'No trial end date set'
                                  }
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSendTrialReminder(company.id);
                                }}
                                disabled={isUpdating}
                              >
                                <Send className="w-3 h-3 mr-1" />
                                Send Reminder
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedCompany(company);
                                  setExtendTrialDialogOpen(true);
                                }}
                              >
                                Extend Trial
                              </Button>
                              <Button 
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => {
                                  setSelectedCompany(company);
                                  handleUpdateSubscription('active');
                                }}
                              >
                                Convert to Paid
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Revenue Analytics */}
                {revenueAnalytics && (
                  <>
                    {/* Key Revenue Metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="border-0 shadow-md">
                        <CardContent className="pt-6 text-center">
                          <p className="text-slate-500 text-sm">Trial Conversion</p>
                          <p className="text-3xl font-bold text-indigo-600">{revenueAnalytics.trial_conversion_rate}%</p>
                        </CardContent>
                      </Card>
                      <Card className="border-0 shadow-md">
                        <CardContent className="pt-6 text-center">
                          <p className="text-slate-500 text-sm">Monthly Churn</p>
                          <p className="text-3xl font-bold text-red-600">{revenueAnalytics.churn_rate}%</p>
                        </CardContent>
                      </Card>
                      <Card className="border-0 shadow-md">
                        <CardContent className="pt-6 text-center">
                          <p className="text-slate-500 text-sm">ARPU</p>
                          <p className="text-3xl font-bold text-green-600">${revenueAnalytics.arpu}</p>
                        </CardContent>
                      </Card>
                      <Card className="border-0 shadow-md">
                        <CardContent className="pt-6 text-center">
                          <p className="text-slate-500 text-sm">Est. LTV</p>
                          <p className="text-3xl font-bold text-purple-600">${revenueAnalytics.ltv}</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* MRR Chart */}
                    <Card className="border-0 shadow-md">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <LineChart className="w-5 h-5" />
                          Revenue Trend (6 Months)
                        </CardTitle>
                        <CardDescription>Monthly Recurring Revenue over time</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {/* Chart Legend */}
                          <div className="flex gap-6 justify-end">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-green-500 rounded-full" />
                              <span className="text-sm text-slate-600">MRR ($)</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-indigo-500 rounded-full" />
                              <span className="text-sm text-slate-600">Customers</span>
                            </div>
                          </div>
                          
                          {/* Bar Chart */}
                          <div className="flex items-end justify-between gap-2 h-48 pt-4">
                            {revenueAnalytics.mrr_history.map((month, idx) => {
                              const maxMrr = Math.max(...revenueAnalytics.mrr_history.map(m => m.mrr), 1);
                              const maxCustomers = Math.max(...revenueAnalytics.mrr_history.map(m => m.customers), 1);
                              const mrrHeight = (month.mrr / maxMrr) * 100;
                              const customerHeight = (month.customers / maxCustomers) * 100;
                              
                              return (
                                <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                                  <div className="flex gap-1 items-end h-36 w-full">
                                    <div 
                                      className="flex-1 bg-green-500 rounded-t-sm transition-all hover:bg-green-600"
                                      style={{ height: `${Math.max(mrrHeight, 5)}%` }}
                                      title={`$${month.mrr} MRR`}
                                    />
                                    <div 
                                      className="flex-1 bg-indigo-500 rounded-t-sm transition-all hover:bg-indigo-600"
                                      style={{ height: `${Math.max(customerHeight, 5)}%` }}
                                      title={`${month.customers} customers`}
                                    />
                                  </div>
                                  <div className="text-xs text-slate-500 text-center">
                                    {month.month}
                                  </div>
                                  <div className="flex gap-2 text-xs">
                                    <span className="text-green-600 font-medium">${month.mrr}</span>
                                    <span className="text-indigo-600 font-medium">{month.customers}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Projections */}
                    <Card className="border-0 shadow-md bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-indigo-100 text-sm">Projected Annual Revenue (10% growth)</p>
                            <p className="text-4xl font-bold mt-2">${revenueAnalytics.projected_arr.toFixed(0)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-indigo-100 text-sm">Current ARR</p>
                            <p className="text-2xl font-bold">${revenueAnalytics.arr.toFixed(0)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </>
            )}
          </TabsContent>

          {/* Announcements Tab */}
          <TabsContent value="announcements" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">System Announcements</h1>
                <p className="text-slate-500">Create banners and broadcast messages to all users</p>
              </div>
              <Button 
                onClick={() => { resetAnnouncementForm(); setAnnouncementDialogOpen(true); }}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="create-announcement-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Announcement
              </Button>
            </div>

            {/* Active Announcements */}
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-green-600" />
                  Active Announcements
                </CardTitle>
                <CardDescription>Currently displayed to users</CardDescription>
              </CardHeader>
              <CardContent>
                {announcements.filter(a => a.is_active).length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No active announcements</p>
                ) : (
                  <div className="space-y-4">
                    {announcements.filter(a => a.is_active).map(announcement => (
                      <div 
                        key={announcement.id} 
                        className={`p-4 rounded-lg border-l-4 ${
                          announcement.type === 'warning' ? 'bg-amber-50 border-amber-500' :
                          announcement.type === 'error' ? 'bg-red-50 border-red-500' :
                          announcement.type === 'success' ? 'bg-green-50 border-green-500' :
                          'bg-blue-50 border-blue-500'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold">{announcement.title}</h4>
                              <Badge variant="outline" className="text-xs">
                                {announcement.target_audience === 'all' ? 'All Users' : 
                                 announcement.target_audience === 'admins' ? 'Admins Only' : 'Employees Only'}
                              </Badge>
                              {announcement.is_banner && (
                                <Badge className="bg-indigo-100 text-indigo-700 text-xs">Banner</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600">{announcement.message}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span>Created {formatDistanceToNow(new Date(announcement.created_at), { addSuffix: true })}</span>
                              {announcement.expires_at && (
                                <span>Expires {format(new Date(announcement.expires_at), 'dd/MM/yyyy')}</span>
                              )}
                              {announcement.email_sent && (
                                <span className="text-green-600">Emailed to {announcement.email_sent_count} users</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleEditAnnouncement(announcement)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => handleToggleAnnouncementActive(announcement)}
                            >
                              <PauseCircle className="w-4 h-4 text-amber-600" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => handleDeleteAnnouncement(announcement.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Inactive Announcements */}
            {announcements.filter(a => !a.is_active).length > 0 && (
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-slate-500">
                    <PauseCircle className="w-5 h-5" />
                    Inactive Announcements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {announcements.filter(a => !a.is_active).map(announcement => (
                      <div key={announcement.id} className="p-3 rounded-lg bg-slate-50 flex justify-between items-center">
                        <div>
                          <h4 className="font-medium text-slate-600">{announcement.title}</h4>
                          <p className="text-sm text-slate-400">{announcement.message.substring(0, 100)}...</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleToggleAnnouncementActive(announcement)}
                          >
                            <PlayCircle className="w-4 h-4 text-green-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleDeleteAnnouncement(announcement.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          <TabsContent value="support" className="space-y-6">
            <Card className="border-0 shadow-md">
              <CardContent className="p-8 text-center">
                <Ticket className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Support Ticket Management</h3>
                <p className="text-slate-500 mb-4">
                  View and manage all customer support tickets from the chatbot escalations.
                </p>
                <Button 
                  onClick={() => navigate('/super-admin/support')}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="open-support-dashboard"
                >
                  Open Support Dashboard
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Profile Settings</h1>
              <p className="text-slate-500">Update your account information</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Update Profile Card */}
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Account Details
                  </CardTitle>
                  <CardDescription>Update your email and name</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleUpdateProfile} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="profile-name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="profile-name"
                          className="pl-10"
                          value={profileForm.name}
                          onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="profile-email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="profile-email"
                          type="email"
                          className="pl-10"
                          value={profileForm.email}
                          onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                      disabled={isUpdatingProfile}
                    >
                      {isUpdatingProfile ? 'Saving...' : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Change Password Card */}
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    Change Password
                  </CardTitle>
                  <CardDescription>Update your password</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="current-password">Current Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="current-password"
                          type="password"
                          className="pl-10"
                          value={passwordForm.current_password}
                          onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-password">New Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="new-password"
                          type="password"
                          className="pl-10"
                          value={passwordForm.new_password}
                          onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Confirm New Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="confirm-password"
                          type="password"
                          className="pl-10"
                          value={passwordForm.confirm_password}
                          onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                      disabled={isChangingPassword}
                    >
                      {isChangingPassword ? 'Changing...' : (
                        <>
                          <Lock className="w-4 h-4 mr-2" />
                          Change Password
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Health Scores Tab */}
          <TabsContent value="health" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Company Health Scores</h1>
                <p className="text-slate-500">Monitor customer health and identify at-risk accounts</p>
              </div>
            </div>

            {/* Health Summary */}
            {healthScores && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="border-0 shadow-md">
                    <CardContent className="pt-6 text-center">
                      <p className="text-slate-500 text-sm">Average Score</p>
                      <p className="text-4xl font-bold text-slate-900">{healthScores.summary.average_score}</p>
                      <p className="text-xs text-slate-400 mt-1">out of 100</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md bg-green-50">
                    <CardContent className="pt-6 text-center">
                      <CheckCircle className="w-6 h-6 mx-auto text-green-600 mb-2" />
                      <p className="text-green-600 text-sm">Healthy</p>
                      <p className="text-3xl font-bold text-green-700">{healthScores.summary.healthy}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md bg-amber-50">
                    <CardContent className="pt-6 text-center">
                      <AlertTriangle className="w-6 h-6 mx-auto text-amber-600 mb-2" />
                      <p className="text-amber-600 text-sm">At Risk</p>
                      <p className="text-3xl font-bold text-amber-700">{healthScores.summary.at_risk}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md bg-red-50">
                    <CardContent className="pt-6 text-center">
                      <XCircle className="w-6 h-6 mx-auto text-red-600 mb-2" />
                      <p className="text-red-600 text-sm">Critical</p>
                      <p className="text-3xl font-bold text-red-700">{healthScores.summary.critical}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Company Health List */}
                <Card className="border-0 shadow-md">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="w-5 h-5" />
                      Company Health Details
                    </CardTitle>
                    <CardDescription>Sorted by health score (lowest first)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-3">
                        {healthScores.companies.map((company) => (
                          <div 
                            key={company.company_id}
                            className={`p-4 rounded-lg border ${
                              company.status === 'critical' ? 'border-red-200 bg-red-50' :
                              company.status === 'at-risk' ? 'border-amber-200 bg-amber-50' :
                              'border-green-200 bg-green-50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${
                                  company.status === 'critical' ? 'bg-red-500' :
                                  company.status === 'at-risk' ? 'bg-amber-500' :
                                  'bg-green-500'
                                }`}>
                                  {company.total_score}
                                </div>
                                <div>
                                  <h3 className="font-semibold text-slate-900">{company.company_name}</h3>
                                  <div className="flex items-center gap-2 mt-1">
                                    {getSubscriptionBadge(company.subscription_status)}
                                    <span className="text-sm text-slate-500">{company.user_count} users</span>
                                  </div>
                                </div>
                              </div>
                              <Badge className={`${
                                company.status === 'critical' ? 'bg-red-100 text-red-700' :
                                company.status === 'at-risk' ? 'bg-amber-100 text-amber-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {company.status.toUpperCase()}
                              </Badge>
                            </div>
                            
                            {/* Score Breakdown */}
                            <div className="mt-4 grid grid-cols-3 gap-4">
                              <div className="text-center p-2 bg-white/50 rounded">
                                <p className="text-xs text-slate-500">Activity</p>
                                <p className="text-lg font-bold">{company.breakdown.activity}/40</p>
                              </div>
                              <div className="text-center p-2 bg-white/50 rounded">
                                <p className="text-xs text-slate-500">Features</p>
                                <p className="text-lg font-bold">{company.breakdown.features}/30</p>
                              </div>
                              <div className="text-center p-2 bg-white/50 rounded">
                                <p className="text-xs text-slate-500">Support</p>
                                <p className="text-lg font-bold">{company.breakdown.support}/30</p>
                              </div>
                            </div>

                            {/* Metrics */}
                            <div className="mt-3 flex flex-wrap gap-2 text-xs">
                              {company.metrics.days_since_activity !== null && (
                                <Badge variant="outline" className="text-slate-600">
                                  Last active: {company.metrics.days_since_activity === 0 ? 'Today' : `${company.metrics.days_since_activity} days ago`}
                                </Badge>
                              )}
                              <Badge variant="outline" className="text-slate-600">
                                {company.metrics.recent_entries} entries (30d)
                              </Badge>
                              {company.metrics.open_tickets > 0 && (
                                <Badge variant="outline" className="text-red-600">
                                  {company.metrics.open_tickets} open tickets
                                </Badge>
                              )}
                              {company.metrics.features_enabled.length > 0 && (
                                <Badge variant="outline" className="text-indigo-600">
                                  {company.metrics.features_enabled.length} features enabled
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Company Detail Dialog */}
      <Dialog open={companyDetailOpen} onOpenChange={setCompanyDetailOpen}>
        <DialogContent className="max-w-2xl">
          {selectedCompany && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl ${selectedCompany.active ? 'bg-indigo-600' : 'bg-slate-400'} text-white flex items-center justify-center font-bold text-lg`}>
                    {selectedCompany.name.charAt(0)}
                  </div>
                  <div>
                    <span>{selectedCompany.name}</span>
                    <div className="flex items-center gap-2 mt-1">
                      {selectedCompany.active ? (
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-700">Inactive</Badge>
                      )}
                      {getSubscriptionBadge(selectedCompany.subscription_status)}
                    </div>
                  </div>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Company Info Grid */}
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium text-slate-500">COMPANY DETAILS</h4>
                  <Button variant="ghost" size="sm" onClick={openEditCompanyDialog}>
                    <Edit className="w-4 h-4 mr-1" />
                    Edit Info
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 uppercase">Industry</p>
                    <p className="font-medium">{selectedCompany.industry || 'Not specified'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 uppercase">Company Size</p>
                    <p className="font-medium">{selectedCompany.employee_count || 'Not specified'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 uppercase">Timezone</p>
                    <p className="font-medium">{selectedCompany.timezone || 'Not set'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 uppercase">Phone</p>
                    <p className="font-medium">{selectedCompany.phone || 'Not provided'}</p>
                  </div>
                </div>

                {/* Address */}
                {selectedCompany.address && (
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 uppercase flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> Address
                    </p>
                    <p className="font-medium">
                      {selectedCompany.address.street}, {selectedCompany.address.city}, {selectedCompany.address.state} {selectedCompany.address.postal_code}, {selectedCompany.address.country}
                    </p>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-indigo-600">{selectedCompany.admin_count}</p>
                    <p className="text-xs text-slate-500">Admins</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{selectedCompany.employee_count_actual}</p>
                    <p className="text-xs text-slate-500">Total Users</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">{selectedCompany.time_entries_count.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">Time Entries</p>
                  </div>
                </div>

                {/* Billing Info */}
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 uppercase">Billing Information</p>
                  <div className="p-3 bg-slate-50 rounded-lg space-y-1">
                    <p><span className="text-slate-500">ABN/Tax ID:</span> {selectedCompany.abn_tax_id || 'Not provided'}</p>
                    <p><span className="text-slate-500">Billing Email:</span> {selectedCompany.billing_email || 'Not provided'}</p>
                  </div>
                </div>

                {/* Marketing Info */}
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 uppercase">Marketing Intel</p>
                  <div className="p-3 bg-slate-50 rounded-lg space-y-1">
                    <p><span className="text-slate-500">Referral Source:</span> {selectedCompany.referral_source || 'Unknown'}</p>
                    <p><span className="text-slate-500">Previous Solution:</span> {selectedCompany.current_solution || 'Unknown'}</p>
                    <p><span className="text-slate-500">Multiple Locations:</span> {selectedCompany.has_multiple_locations ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2 flex-wrap">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    fetchCompanyNotesAndTags(selectedCompany.id);
                    setNotesTagsDialogOpen(true);
                  }}
                  data-testid="notes-tags-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Notes & Tags
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    fetchCompanySubdomain(selectedCompany.id);
                    setSubdomainDialogOpen(true);
                  }}
                  data-testid="subdomain-btn"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Subdomain
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleExportCompanyData(selectedCompany.id, selectedCompany.name)}
                  disabled={isUpdating}
                  data-testid="export-company-data-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Data
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    fetchCompanyFeatureFlags(selectedCompany.id);
                    setFeatureFlagsDialogOpen(true);
                  }}
                  data-testid="feature-flags-btn"
                >
                  <Settings2 className="w-4 h-4 mr-2" />
                  Features
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    fetchCompanyUsers(selectedCompany.id);
                    setImpersonationDialogOpen(true);
                  }}
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  View as User
                </Button>
                <Button variant="outline" onClick={() => {
                  setSubscriptionDialogOpen(true);
                }}>
                  Change Subscription
                </Button>
                {selectedCompany.subscription_status === 'trial' && (
                  <Button variant="outline" onClick={() => {
                    setExtendTrialDialogOpen(true);
                  }}>
                    Extend Trial
                  </Button>
                )}
                <Button onClick={() => navigate('/super-admin/companies')}>
                  Manage Company
                </Button>
                {/* Delete Company Button - only for companies without time entries */}
                <Button 
                  variant="outline" 
                  className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={handleDeleteCompany}
                  disabled={isUpdating}
                  data-testid="delete-company-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Company
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Subscription Update Dialog */}
      <Dialog open={subscriptionDialogOpen} onOpenChange={setSubscriptionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Subscription</DialogTitle>
            <DialogDescription>
              Change subscription status for {selectedCompany?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col"
              onClick={() => handleUpdateSubscription('trial')}
              disabled={isUpdating}
            >
              <PlayCircle className="w-6 h-6 mb-2 text-blue-600" />
              <span>Trial</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col"
              onClick={() => handleUpdateSubscription('active')}
              disabled={isUpdating}
            >
              <CheckCircle className="w-6 h-6 mb-2 text-green-600" />
              <span>Paid / Active</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col"
              onClick={() => handleUpdateSubscription('suspended')}
              disabled={isUpdating}
            >
              <AlertTriangle className="w-6 h-6 mb-2 text-amber-600" />
              <span>Suspended</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col"
              onClick={() => handleUpdateSubscription('cancelled')}
              disabled={isUpdating}
            >
              <XCircle className="w-6 h-6 mb-2 text-red-600" />
              <span>Cancelled</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Extend Trial Dialog */}
      <Dialog open={extendTrialDialogOpen} onOpenChange={setExtendTrialDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Extend Trial Period</DialogTitle>
            <DialogDescription>
              Extend the trial for {selectedCompany?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Extension Period (days)</Label>
              <Select value={trialDays.toString()} onValueChange={(v) => setTrialDays(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendTrialDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleExtendTrial} disabled={isUpdating}>
              {isUpdating ? 'Extending...' : `Extend by ${trialDays} days`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Impersonation Dialog */}
      <Dialog open={impersonationDialogOpen} onOpenChange={setImpersonationDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              View as User
            </DialogTitle>
            <DialogDescription>
              Select a user to see the app from their perspective. All actions will be logged.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {loadingUsers ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : companyUsers.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No users found in this company</p>
            ) : (
              <ScrollArea className="h-[300px]">
                <div className="space-y-2">
                  {companyUsers.map(u => (
                    <div 
                      key={u.id}
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                          u.role === 'company_admin' ? 'bg-indigo-600' : 
                          u.is_manager ? 'bg-purple-600' : 'bg-slate-500'
                        }`}>
                          {u.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{u.name}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                          <Badge variant="outline" className="text-xs mt-1">
                            {u.role === 'company_admin' ? 'Admin' : u.is_manager ? 'Manager' : 'Employee'}
                          </Badge>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => handleImpersonateUser(u.id)}
                        disabled={isUpdating}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpersonationDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Company Info Dialog */}
      <Dialog open={editCompanyDialogOpen} onOpenChange={setEditCompanyDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Company Information
            </DialogTitle>
            <DialogDescription>
              Update details for {selectedCompany?.name}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 py-4 pr-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Industry</Label>
                  <Select 
                    value={editCompanyForm.industry} 
                    onValueChange={(v) => setEditCompanyForm(prev => ({...prev, industry: v}))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agriculture">Agriculture</SelectItem>
                      <SelectItem value="construction">Construction</SelectItem>
                      <SelectItem value="healthcare">Healthcare</SelectItem>
                      <SelectItem value="hospitality">Hospitality</SelectItem>
                      <SelectItem value="manufacturing">Manufacturing</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                      <SelectItem value="technology">Technology</SelectItem>
                      <SelectItem value="transport">Transport & Logistics</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Company Size</Label>
                  <Select 
                    value={editCompanyForm.company_size} 
                    onValueChange={(v) => setEditCompanyForm(prev => ({...prev, company_size: v}))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1-10 employees</SelectItem>
                      <SelectItem value="11-50">11-50 employees</SelectItem>
                      <SelectItem value="51-200">51-200 employees</SelectItem>
                      <SelectItem value="201-500">201-500 employees</SelectItem>
                      <SelectItem value="500+">500+ employees</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select 
                    value={editCompanyForm.timezone} 
                    onValueChange={(v) => setEditCompanyForm(prev => ({...prev, timezone: v}))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pacific/Auckland">NZ - Auckland</SelectItem>
                      <SelectItem value="Pacific/Chatham">NZ - Chatham Islands</SelectItem>
                      <SelectItem value="Australia/Sydney">AU - Sydney</SelectItem>
                      <SelectItem value="Australia/Melbourne">AU - Melbourne</SelectItem>
                      <SelectItem value="Australia/Brisbane">AU - Brisbane</SelectItem>
                      <SelectItem value="Australia/Perth">AU - Perth</SelectItem>
                      <SelectItem value="Europe/London">UK - London</SelectItem>
                      <SelectItem value="America/New_York">US - New York</SelectItem>
                      <SelectItem value="America/Los_Angeles">US - Los Angeles</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input 
                    value={editCompanyForm.phone}
                    onChange={(e) => setEditCompanyForm(prev => ({...prev, phone: e.target.value}))}
                    placeholder="+64 21 123 4567"
                  />
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-sm text-slate-500 mb-3">BILLING INFORMATION</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ABN / Tax ID</Label>
                    <Input 
                      value={editCompanyForm.abn_tax_id}
                      onChange={(e) => setEditCompanyForm(prev => ({...prev, abn_tax_id: e.target.value}))}
                      placeholder="123-456-789"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Billing Email</Label>
                    <Input 
                      type="email"
                      value={editCompanyForm.billing_email}
                      onChange={(e) => setEditCompanyForm(prev => ({...prev, billing_email: e.target.value}))}
                      placeholder="billing@company.com"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-sm text-slate-500 mb-3">MARKETING INTEL</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Referral Source</Label>
                    <Select 
                      value={editCompanyForm.referral_source} 
                      onValueChange={(v) => setEditCompanyForm(prev => ({...prev, referral_source: v}))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="How did they find us?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="google">Google Search</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                        <SelectItem value="linkedin">LinkedIn</SelectItem>
                        <SelectItem value="referral">Customer Referral</SelectItem>
                        <SelectItem value="partner">Partner</SelectItem>
                        <SelectItem value="tradeshow">Trade Show</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Previous Solution</Label>
                    <Select 
                      value={editCompanyForm.previous_solution} 
                      onValueChange={(v) => setEditCompanyForm(prev => ({...prev, previous_solution: v}))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="What were they using?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paper">Paper / Manual</SelectItem>
                        <SelectItem value="excel">Excel / Spreadsheets</SelectItem>
                        <SelectItem value="deputy">Deputy</SelectItem>
                        <SelectItem value="tanda">Tanda</SelectItem>
                        <SelectItem value="timely">Timely</SelectItem>
                        <SelectItem value="other_software">Other Software</SelectItem>
                        <SelectItem value="none">None / New Business</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <input
                    type="checkbox"
                    id="multiple_locations"
                    checked={editCompanyForm.multiple_locations}
                    onChange={(e) => setEditCompanyForm(prev => ({...prev, multiple_locations: e.target.checked}))}
                    className="rounded border-slate-300"
                  />
                  <Label htmlFor="multiple_locations" className="cursor-pointer">Has multiple locations</Label>
                </div>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCompanyDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCompanyInfo} disabled={isUpdating}>
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feature Flags Dialog */}
      <Dialog open={featureFlagsDialogOpen} onOpenChange={setFeatureFlagsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Feature Flags
            </DialogTitle>
            <DialogDescription>
              Enable or disable features for {companyFeatureFlags?.company_name || 'this company'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {companyFeatureFlags ? (
              <div className="space-y-4">
                {Object.entries(FEATURE_FLAG_LABELS).map(([key, { label, description }]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{label}</p>
                      <p className="text-xs text-slate-500">{description}</p>
                    </div>
                    <Switch
                      checked={companyFeatureFlags.feature_flags[key] ?? true}
                      onCheckedChange={() => handleToggleFeatureFlag(key, companyFeatureFlags.feature_flags[key])}
                      data-testid={`feature-flag-${key}`}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeatureFlagsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Announcement Dialog */}
      <Dialog open={announcementDialogOpen} onOpenChange={(open) => { if (!open) resetAnnouncementForm(); setAnnouncementDialogOpen(open); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}</DialogTitle>
            <DialogDescription>
              {editingAnnouncement ? 'Update the announcement details' : 'Create a new system-wide announcement or broadcast'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                placeholder="Announcement title"
                value={announcementForm.title}
                onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                data-testid="announcement-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                placeholder="Write your announcement message..."
                rows={4}
                value={announcementForm.message}
                onChange={(e) => setAnnouncementForm({...announcementForm, message: e.target.value})}
                data-testid="announcement-message-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={announcementForm.type} 
                  onValueChange={(v) => setAnnouncementForm({...announcementForm, type: v})}
                >
                  <SelectTrigger data-testid="announcement-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info (Blue)</SelectItem>
                    <SelectItem value="success">Success (Green)</SelectItem>
                    <SelectItem value="warning">Warning (Yellow)</SelectItem>
                    <SelectItem value="error">Error (Red)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Target Audience</Label>
                <Select 
                  value={announcementForm.target_audience} 
                  onValueChange={(v) => setAnnouncementForm({...announcementForm, target_audience: v})}
                >
                  <SelectTrigger data-testid="announcement-audience-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="admins">Admins Only</SelectItem>
                    <SelectItem value="employees">Employees Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Expires At (Optional)</Label>
              <Input
                type="datetime-local"
                value={announcementForm.expires_at ? announcementForm.expires_at.slice(0, 16) : ''}
                onChange={(e) => setAnnouncementForm({...announcementForm, expires_at: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                data-testid="announcement-expires-input"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  id="announcement-is-banner"
                  name="is_banner"
                  checked={announcementForm.is_banner}
                  onChange={(e) => setAnnouncementForm({...announcementForm, is_banner: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm">Show as banner on dashboard</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  id="announcement-is-dismissible"
                  name="is_dismissible"
                  checked={announcementForm.is_dismissible}
                  onChange={(e) => setAnnouncementForm({...announcementForm, is_dismissible: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm">Allow dismiss</span>
              </label>
            </div>
            {!editingAnnouncement && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    id="announcement-send-email"
                    name="send_email"
                    checked={announcementForm.send_email}
                    onChange={(e) => setAnnouncementForm({...announcementForm, send_email: e.target.checked})}
                    className="rounded"
                  />
                  <div>
                    <span className="text-sm font-medium">Also send as email broadcast</span>
                    <p className="text-xs text-amber-700">This will send an email to all targeted users immediately</p>
                  </div>
                </label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetAnnouncementForm(); setAnnouncementDialogOpen(false); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateAnnouncement}
              disabled={isUpdating || !announcementForm.title || !announcementForm.message}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-announcement-btn"
            >
              {isUpdating ? 'Saving...' : (editingAnnouncement ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notes & Tags Dialog */}
      <Dialog open={notesTagsDialogOpen} onOpenChange={setNotesTagsDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Notes & Tags
            </DialogTitle>
            <DialogDescription>
              Internal notes and tags for {selectedCompany?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-6">
            {loadingNotes ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <>
                {/* Tags Section */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Tags</Label>
                  <div className="flex flex-wrap gap-2">
                    {companyTags.map(tag => (
                      <Badge 
                        key={tag} 
                        className="bg-indigo-100 text-indigo-700 pr-1 flex items-center gap-1"
                      >
                        {tag}
                        <button 
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 hover:bg-indigo-200 rounded-full p-0.5"
                        >
                          <XCircle className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {companyTags.length === 0 && (
                      <span className="text-sm text-slate-500">No tags yet</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a tag (e.g., vip, at-risk, new-customer)"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleUpdateTags()}
                      className="flex-1"
                      data-testid="new-tag-input"
                    />
                    <Button 
                      onClick={handleUpdateTags}
                      disabled={!newTag.trim()}
                      size="sm"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Internal Notes</Label>
                  <ScrollArea className="h-[200px] border rounded-lg p-3">
                    {companyNotes.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">No notes yet</p>
                    ) : (
                      <div className="space-y-3">
                        {companyNotes.map(note => (
                          <div key={note.id} className="p-3 bg-slate-50 rounded-lg">
                            <p className="text-sm">{note.text}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-slate-500">
                                {note.created_by_name || note.created_by} • {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                              </span>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => handleDeleteNote(note.id)}
                              >
                                <Trash2 className="w-3 h-3 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                  <div className="flex gap-2">
                    <Textarea
                      placeholder="Add a note about this company..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={2}
                      className="flex-1"
                      data-testid="new-note-input"
                    />
                    <Button 
                      onClick={handleAddNote}
                      disabled={!newNote.trim()}
                      className="self-end"
                    >
                      Add Note
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesTagsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subdomain Dialog */}
      <Dialog open={subdomainDialogOpen} onOpenChange={setSubdomainDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Custom Subdomain
            </DialogTitle>
            <DialogDescription>
              Set a custom subdomain for {selectedCompany?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {loadingSubdomain ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <>
                {subdomainInfo?.subdomain ? (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-800">Current Subdomain</p>
                        <p className="text-lg font-bold text-green-700">{subdomainInfo.full_url}</p>
                        {subdomainInfo.subdomain_verified && (
                          <Badge className="bg-green-100 text-green-700 mt-1">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleRemoveSubdomain}
                        disabled={isUpdating}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Subdomain</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="yourcompany"
                          value={companySubdomain}
                          onChange={(e) => setCompanySubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                          className="flex-1"
                          data-testid="subdomain-input"
                        />
                        <span className="text-slate-500">.clockinpro.live</span>
                      </div>
                      <p className="text-xs text-slate-500">
                        Only lowercase letters, numbers, and hyphens allowed
                      </p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-700">
                        <strong>Note:</strong> Subdomains allow companies to access ClockIn Pro via a custom URL. 
                        This is primarily for branding purposes.
                      </p>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubdomainDialogOpen(false)}>
              Cancel
            </Button>
            {!subdomainInfo?.subdomain && (
              <Button 
                onClick={handleSetSubdomain}
                disabled={isUpdating || !companySubdomain.trim()}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {isUpdating ? 'Setting...' : 'Set Subdomain'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboardNew;
